
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { createAircraftType } from "@/services/aircraftService";
import { supabase } from "@/integrations/supabase/client";
import { AircraftType } from "@/types/flight";

const aircraftTypeSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  code: z.string().min(1, "Code must be at least 1 character").max(10, "Code must be at most 10 characters"),
});

type FormValues = z.infer<typeof aircraftTypeSchema>;

interface AircraftTypeFormProps {
  aircraftType?: AircraftType | null;
  onSubmit: () => void;
}

export function AircraftTypeForm({ aircraftType, onSubmit }: AircraftTypeFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(aircraftTypeSchema),
    defaultValues: {
      name: aircraftType?.name || "",
      code: aircraftType?.code || "",
    },
  });

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    
    try {
      if (aircraftType) {
        // Update existing aircraft type
        const { error } = await supabase
          .from('aircraft_types')
          .update({
            name: values.name,
            code: values.code,
          })
          .eq('id', aircraftType.id);

        if (error) throw error;

        toast({
          title: "Aircraft type updated",
          description: `${values.name} has been updated successfully.`,
        });
      } else {
        // Create new aircraft type
        await createAircraftType({
          name: values.name,
          code: values.code,
        });

        toast({
          title: "Aircraft type created",
          description: `${values.name} has been created successfully.`,
        });
      }
      
      onSubmit();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save aircraft type",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Aircraft type name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Code</FormLabel>
              <FormControl>
                <Input placeholder="Aircraft type code (e.g., B737)" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : aircraftType ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
